import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE, BACKEND_URL } from '../config';
import './MenuPage.css';

const MenuPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState('tr');
  const [activeCategory, setActiveCategory] = useState(null); // null = grid view
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('qm-dark') === '1');
  const [instagramMenu, setInstagramMenu] = useState(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/menu/by-slug/${slug}`)
      .then(r => r.json())
      .then(res => {
        if (res.business) setBusiness(res.business);
        if (res.categories) setData(res);
        if (res.instagramMenu) setInstagramMenu(res.instagramMenu);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (business) document.title = `${business.name} - ${lang === 'tr' ? 'Menü' : 'Menu'}`;
    return () => { document.title = 'Dihiz'; };
  }, [business, lang]);

  useEffect(() => {
    document.documentElement.setAttribute('data-qm-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('qm-dark', darkMode ? '1' : '0');
  }, [darkMode]);

  const t = useCallback((item, field) => {
    if (!item) return '';
    const localized = item[`${field}_${lang}`];
    if (localized) return localized;
    const fallback = item[`${field}_${lang === 'tr' ? 'en' : 'tr'}`];
    return fallback || item[field] || '';
  }, [lang]);

  const getPrice = useCallback((item) => {
    if (!item) return null;
    const p = item.price ?? item.price_tr;
    if (p === null || p === undefined || p === '') return null;
    return p;
  }, []);

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const categories = data?.categories || [];

  const allItems = categories.flatMap((cat, catIdx) =>
    (cat.items || []).map(item => ({ ...item, categoryName: t(cat, 'name'), catIdx }))
  );

  const filteredItems = searchQuery.trim()
    ? allItems.filter(item => {
        const q = searchQuery.toLowerCase();
        return t(item, 'name').toLowerCase().includes(q) ||
               t(item, 'description').toLowerCase().includes(q);
      })
    : [];

  const openSearch = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  if (loading) {
    return (
      <div className="qm qm-loading-wrap">
        <div className="qm-loading-spinner" />
        <p>{lang === 'tr' ? 'Menü yükleniyor...' : 'Loading menu...'}</p>
      </div>
    );
  }

  if (!business) {
    return <div className="qm qm-loading-wrap"><p>İşletme bulunamadı.</p></div>;
  }

  const currentCat = activeCategory !== null ? categories[activeCategory] : null;

  return (
    <div className={`qm ${darkMode ? 'dark' : ''}`}>
      {/* ═══ HEADER ═══ */}
      <header className="qm-header">
        <div className="qm-header-inner">
          <div className="qm-header-center">
            {business.image && (
              <img src={getImageUrl(business.image)} alt={business.name} className="qm-logo" />
            )}
            <div>
              <h1 className="qm-title">{business.name}</h1>
              <p className="qm-subtitle">QR {lang === 'tr' ? 'Menü' : 'Menu'}</p>
            </div>
          </div>
          <div className="qm-header-actions">
            <button className="qm-icon-btn" onClick={() => setDarkMode(!darkMode)} aria-label="Tema">
              {darkMode ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
              )}
            </button>
            <button className="qm-icon-btn" onClick={openSearch} aria-label="Ara">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
            </button>
            <div className="qm-lang-select">
              <select value={lang} onChange={e => setLang(e.target.value)}>
                <option value="tr">TR</option>
                <option value="en">EN</option>
              </select>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
            </div>
          </div>
        </div>
      </header>

      <main className="qm-main">
        {/* ═══ INSTAGRAM MENU (Kent Lokantası vb.) ═══ */}
        {instagramMenu && instagramMenu.length > 0 && (() => {
          const post = instagramMenu[0];
          const postImg = post.image || post.image_url || '';
          const postDate = post.timestamp || post.taken_at || 0;
          return (
            <div className="qm-ig-menu">
              <div className="qm-ig-menu-header">
                <h2 className="qm-ig-menu-title">
                  {lang === 'tr' ? 'Güncel Menümüz' : 'Current Menu'}
                </h2>
                <p className="qm-ig-menu-desc">
                  {lang === 'tr'
                    ? 'Menümüz haftalık olarak güncellenmektedir.'
                    : 'Our menu is updated weekly.'}
                </p>
                {postDate > 0 && (
                  <span className="qm-ig-menu-date" style={{ display: 'inline-block', marginTop: 6 }}>
                    {new Date(postDate * 1000).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                )}
              </div>
              {postImg && (
                <img src={postImg} alt="Güncel Menü" className="qm-ig-menu-full" />
              )}
            </div>
          );
        })()}

        {/* ═══ CATEGORY GRID VIEW ═══ */}
        {!instagramMenu && activeCategory === null && (
          <div className="qm-cat-grid">
            {categories.map((cat, idx) => {
              const img = cat.image ? getImageUrl(cat.image) :
                          cat.items?.find(it => it.image)?.image ? getImageUrl(cat.items.find(it => it.image).image) : null;
              return (
                <div
                  key={idx}
                  className="qm-cat-card"
                  onClick={() => { setActiveCategory(idx); window.scrollTo({ top: 0 }); }}
                  style={{ animationDelay: `${idx * 0.06}s` }}
                >
                  <div className="qm-cat-card-img">
                    {img ? (
                      <img src={img} alt="" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="qm-cat-card-placeholder">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M18 8h1a4 4 0 010 8h-1M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/>
                          <path d="M6 1v3M10 1v3M14 1v3"/>
                        </svg>
                      </div>
                    )}
                    <div className="qm-cat-card-overlay" />
                  </div>
                  <div className="qm-cat-card-label">
                    <span>{t(cat, 'name')}</span>
                    <span className="qm-cat-card-count">{cat.items?.length || 0}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ CATEGORY DETAIL VIEW ═══ */}
        {!instagramMenu && currentCat && (
          <div className="qm-detail-view">
            <div className="qm-back-bar">
              <button className="qm-back-btn" onClick={() => setActiveCategory(null)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                {lang === 'tr' ? 'Geri Dön' : 'Back'}
              </button>
              <h2 className="qm-cat-title">{t(currentCat, 'name')}</h2>
            </div>

            {/* Category nav pills */}
            <div className="qm-cat-pills">
              {categories.map((cat, idx) => (
                <button
                  key={idx}
                  className={`qm-pill ${activeCategory === idx ? 'active' : ''}`}
                  onClick={() => { setActiveCategory(idx); window.scrollTo({ top: 0 }); }}
                >
                  {t(cat, 'name')}
                </button>
              ))}
            </div>

            {t(currentCat, 'description') && (
              <p className="qm-cat-desc">{t(currentCat, 'description')}</p>
            )}

            <div className="qm-items-list">
              {currentCat.items?.map((item, idx) => {
                const name = t(item, 'name');
                if (!name) return null;
                const price = getPrice(item);
                const desc = t(item, 'description');
                const img = item.image ? getImageUrl(item.image) : null;

                return (
                  <div
                    key={idx}
                    className="qm-item-card"
                    onClick={() => setSelectedItem({ ...item, name, desc, price, img })}
                    style={{ animationDelay: `${idx * 0.04}s` }}
                  >
                    {img && (
                      <div className="qm-item-img">
                        <img src={img} alt={name} loading="lazy" />
                      </div>
                    )}
                    <div className="qm-item-info">
                      <h3 className="qm-item-name">{name}</h3>
                      {desc && <p className="qm-item-desc">{desc}</p>}
                    </div>
                    {price !== null && (
                      <div className="qm-item-price">{price} <small>₺</small></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* ═══ FOOTER BAR ═══ */}
      <footer className="qm-footer">
        <a href="/" className="qm-footer-site-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
          <span>{lang === 'tr' ? 'Web Sitemizi Keşfedin' : 'Visit Our Website'}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </a>
      </footer>

      {/* ═══ SEARCH MODAL ═══ */}
      {searchOpen && (
        <div className="qm-overlay" onClick={() => { setSearchOpen(false); setSearchQuery(''); }}>
          <div className="qm-search-modal" onClick={e => e.stopPropagation()}>
            <div className="qm-search-bar">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input
                ref={searchInputRef}
                type="text"
                placeholder={lang === 'tr' ? 'Menüde ara...' : 'Search menu...'}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              <button onClick={() => { setSearchOpen(false); setSearchQuery(''); }}>✕</button>
            </div>
            <div className="qm-search-results">
              {searchQuery.trim() && filteredItems.length === 0 && (
                <div className="qm-search-empty">{lang === 'tr' ? 'Sonuç bulunamadı' : 'No results'}</div>
              )}
              {filteredItems.map((item, idx) => (
                <div key={idx} className="qm-search-row" onClick={() => { setActiveCategory(item.catIdx); setSearchOpen(false); setSearchQuery(''); }}>
                  {item.image && <img src={getImageUrl(item.image)} alt="" className="qm-search-thumb" />}
                  <div className="qm-search-info">
                    <strong>{t(item, 'name')}</strong>
                    <small>{item.categoryName}</small>
                  </div>
                  {getPrice(item) !== null && <span className="qm-search-price">{getPrice(item)} ₺</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ ITEM DETAIL MODAL ═══ */}
      {selectedItem && (
        <div className="qm-overlay" onClick={() => setSelectedItem(null)}>
          <div className="qm-item-modal" onClick={e => e.stopPropagation()}>
            <button className="qm-modal-close" onClick={() => setSelectedItem(null)}>✕</button>
            {selectedItem.img && (
              <div className="qm-modal-img">
                <img src={selectedItem.img} alt={selectedItem.name} />
              </div>
            )}
            <div className="qm-modal-body">
              <h3>{selectedItem.name}</h3>
              {selectedItem.desc && <p>{selectedItem.desc}</p>}
              {selectedItem.price !== null && (
                <div className="qm-modal-price">{selectedItem.price} ₺</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuPage;
